/**
 * @documental
 * @export Field
 * @name
 * Radio.Field
 */

import { useEffect, useMemo, FC } from "react";
import { useController, useFormContext } from "react-hook-form";
import { FieldBaseProps } from "../FieldBaseProps";
import { Input } from "./Input";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { NoPaddingFormComponents } from "../shared";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends Omit<FieldBaseProps<boolean | null>, "required">, NoPaddingFormComponents {
  // We remove the required property because it makes no sense on radio buttons. Their value is automatically implicit.
}

/**
 * A React Hook Form compatible Radio component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, defaultValue = null, validator } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validator,
    }),
    [validator, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { isSubmitting } = formState;
  const { invalid, error } = fieldState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur} />
      </ValidityGroup>
    </SubmitGroup>
  );
};
